import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import SEO from '../components/SEO';
import ApplyLink from '../components/ApplyLink';
import { posthog } from '../lib/posthog';
import './NotFound.css';

/**
 * The page for a path that does not exist.
 *
 * History: App.tsx once had no catch-all, so unknown paths rendered an empty
 * <main> as HTTP 200 — a soft 404 Google indexed and ranked (/our-kitchens/%20
 * at position 4.3). Then for ten days three ad sitelinks pointed at /pricing,
 * /how-it-works and /contact, none of which exist, and nothing noticed because
 * the SPA answers 200 for everything (CMSY-17, COCO-94).
 *
 * So this page does three things a plain 404 does not:
 *  - reports itself to PostHog (`page_not_found`, with the path and referrer),
 *    so a dead link somebody is paying for shows up in the funnel the same day;
 *  - takes the visitor home after a short countdown, since most arrive from a
 *    stale link and the homepage is the right next step — but stops the clock
 *    on the first touch, scroll or keypress so nobody is yanked mid-read;
 *  - puts the two conversions (apply, call) one tap away.
 */
const RETURN_SECONDS = 10;

const NotFound = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [seconds, setSeconds] = useState(RETURN_SECONDS);
  const [paused, setPaused] = useState(false);
  const reported = useRef(false);

  // One report per landing, never per re-render.
  useEffect(() => {
    if (reported.current) return;
    reported.current = true;
    try {
      if (posthog.__loaded) posthog.capture('page_not_found', { path: pathname, referrer: document.referrer || null });
    } catch { /* analytics must never break the page */ }
  }, [pathname]);

  // The countdown. Any sign the visitor is reading or choosing cancels it.
  useEffect(() => {
    if (paused) return undefined;
    const stop = () => setPaused(true);
    const opts: AddEventListenerOptions = { passive: true, once: true };
    window.addEventListener('pointerdown', stop, opts);
    window.addEventListener('keydown', stop, opts);
    window.addEventListener('wheel', stop, opts);
    window.addEventListener('touchmove', stop, opts);
    const tick = setInterval(() => setSeconds((s) => s - 1), 1000);
    return () => {
      clearInterval(tick);
      window.removeEventListener('pointerdown', stop);
      window.removeEventListener('keydown', stop);
      window.removeEventListener('wheel', stop);
      window.removeEventListener('touchmove', stop);
    };
  }, [paused]);

  useEffect(() => {
    if (!paused && seconds <= 0) navigate('/', { replace: true });
  }, [seconds, paused, navigate]);

  return (
    <div className="notfound">
      <SEO
        title="Page not found — Commissary Connect"
        description="That page has moved or no longer exists. Find our Vancouver commercial kitchen facilities and application here."
        noindex
      />
      <div className="notfound-inner">
        <p className="notfound-eyebrow">404</p>
        <h1>We couldn't find that page.</h1>
        <p className="notfound-lead">
          The link may be old or mistyped. Everything below is current.
        </p>

        <p className="notfound-countdown" aria-live="polite">
          {paused
            ? 'Take your time — pick a page below.'
            : `Taking you to the homepage in ${seconds} second${seconds === 1 ? '' : 's'}.`}
          {!paused && (
            <button type="button" className="notfound-stay" onClick={() => setPaused(true)}>
              Stay here
            </button>
          )}
        </p>

        <div className="notfound-actions">
          <Link to="/" className="notfound-btn notfound-btn-primary">Go to the homepage</Link>
          <ApplyLink className="notfound-btn notfound-btn-secondary" label="notfound_apply">
            Apply for kitchen space
          </ApplyLink>
          <a className="notfound-btn notfound-btn-secondary" href="tel:7785587272" data-phone-label="notfound">
            Call 778-558-7272
          </a>
        </div>

        <nav className="notfound-links" aria-label="Main pages">
          <Link to="/our-kitchens" className="notfound-card">
            <strong>Our kitchens</strong>
            <span>Our commercial kitchen facilities.</span>
          </Link>
          <Link to="/faq" className="notfound-card">
            <strong>How it works</strong>
            <span>Hours, storage, licensing and what is included.</span>
          </Link>
          <Link to="/story" className="notfound-card">
            <strong>Our story</strong>
            <span>Why we built shared commercial kitchen space in Vancouver.</span>
          </Link>
          <Link to="/members" className="notfound-card">
            <strong>Members</strong>
            <span>Who cooks with us.</span>
          </Link>
        </nav>

        {/* Shown so a person reporting a broken link can say which one, and so
            the path is visible in a screenshot. Not a stack trace. */}
        <p className="notfound-path">
          Requested: <code>{pathname}</code>
        </p>
      </div>
    </div>
  );
};

export default NotFound;
